import React, { useState, useEffect } from 'react';
import {
  Box, Button, CircularProgress, Alert, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
} from '@mui/material';
import { Close, Image as ImageIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { adminGetTransactionDetail, getEvidencePhotoUrl } from 'api/wallet';
import AuthenticatedImage from 'components/AuthenticatedImage';
import { formatPaymentSource } from 'utils/formatWalletTxnDetail';

// Extracted from scenes/wallet/accountTransactions.jsx (was module-local there) so
// other wallet-admin transaction lists — e.g. components/WalletHistoryDialog.jsx —
// can reuse the same detail view instead of duplicating it.
const FONT = "'Plus Jakarta Sans', 'Noto Sans Lao', system-ui, sans-serif";
const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
const CUR_SYM = { THB: '฿', LAK: '₭', USD: '$' };
const money = (n, c) => `${CUR_SYM[c] || ''}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtD = (d) => (d ? new Date(d).toLocaleDateString() : '');

const DetailRow = ({ label, value, mono }) => (
  <Box display="flex" gap={1} py={0.9} sx={{ borderBottom: '1px solid #f4f2f8' }}>
    <Box sx={{ fontSize: 12.5, color: '#9b96a6', minWidth: 130, flexShrink: 0 }}>{label}</Box>
    <Box sx={{ fontSize: 13, fontWeight: 600, color: '#2c2937', wordBreak: mono ? 'break-all' : 'normal', fontFamily: mono ? MONO : FONT }}>
      {value || '—'}
    </Box>
  </Box>
);

export default function WalletTransactionAdminDetailDialog({ open, onClose, txId }) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !txId) return;
    const fetch = async () => {
      try {
        setLoading(true);
        setError('');
        setDetail(null);
        const result = await adminGetTransactionDetail(txId);
        setDetail(result.data);
      } catch (err) {
        setError(err.message || 'Failed to load details');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [open, txId]);

  const tx = detail?.transaction;
  const req = detail?.topupRequest;
  const out = tx?.txn_type === 'debit';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px', fontFamily: FONT } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 800 }}>
        {t('wallet.admin.transactionDetail')}
        <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && <Box display="flex" justifyContent="center" py={4}><CircularProgress color="secondary" /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
        {tx && (
          <>
            <Box sx={{ textAlign: 'center', py: '20px', mb: 2, borderRadius: '16px', background: out ? '#fdeef0' : '#eafaf0' }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: '999px', border: `1.5px solid ${out ? '#fca5a5' : '#86efac'}`, background: '#fff', color: out ? '#c0343a' : '#15803d', textTransform: 'uppercase', mb: 1.5 }}>
                {tx.txn_type}
              </Box>
              <Box sx={{ fontFamily: MONO, fontSize: 32, fontWeight: 700, color: out ? '#c0343a' : '#117a4f' }}>
                {out ? '-' : '+'}{money(tx.amount, tx.currency)}
              </Box>
              <Box sx={{ fontSize: 12, color: '#9b96a6', mt: '4px' }}>{tx.currency}</Box>
            </Box>

            <DetailRow label={t('wallet.admin.transactionId')} value={tx.id} mono />
            <DetailRow label={t('wallet.admin.purpose')} value={tx.purpose} />
            <DetailRow label="Description" value={tx.description} />
            <DetailRow label={t('wallet.admin.balanceBefore')} value={money(tx.balanceBefore, tx.currency)} />
            <DetailRow label={t('wallet.admin.balanceAfter')} value={money(tx.balanceAfter, tx.currency)} />
            <DetailRow label={t('wallet.admin.reference')} value={tx.reference} mono />
            {tx.externalRef && <DetailRow label={t('wallet.admin.externalRef')} value={tx.externalRef} mono />}
            <DetailRow label={t('wallet.admin.remark')} value={tx.remark} />
            {tx.purpose === 'deposit' && tx.paymentSource && (
              <DetailRow label={t('wallet.admin.paymentSource')} value={formatPaymentSource(tx.paymentSource, t)} />
            )}
            {tx.purpose === 'deposit' && tx.stationName && (
              <DetailRow label={t('wallet.admin.selectStation')} value={tx.stationName} />
            )}
            {tx.purpose === 'purchase' && tx.platform && (
              <DetailRow
                label={t('wallet.admin.platform')}
                value={tx.platform === 'Other' && tx.platformOther ? tx.platformOther : t(`wallet.admin.platforms.${tx.platform}`)}
              />
            )}
            {tx.purpose === 'purchase' && tx.orderDate && (
              <DetailRow label={t('wallet.admin.orderDate')} value={fmtD(tx.orderDate)} />
            )}
            {tx.purpose === 'payment' && typeof tx.itemCount === 'number' && (
              <DetailRow label={t('financial.parcels')} value={tx.itemCount} />
            )}
            {tx.purpose === 'payment' && tx.itemCount === 1 && tx.trackingNumber && (
              <DetailRow label={t('financial.tracking')} value={tx.trackingNumber} mono />
            )}
            <DetailRow label={t('wallet.admin.performedBy')} value={`${tx.actionBy?.name} (${tx.actionBy?.userType})`} />
            <DetailRow label={t('wallet.admin.date')} value={fmtD(tx.createdAt)} />

            {/* Evidence attached directly to the leg — purchase orders and refunds
                (unlike a deposit, which has no evidence of its own and instead
                reads it off the linked WalletTopupRequest below). */}
            {tx.evidencePhoto && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Box sx={{ fontSize: 11.5, color: '#9b96a6', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>{t('wallet.admin.sections.evidence')}</Box>
                </Divider>
                <Box>
                  <Box sx={{ fontSize: 12, color: '#9b96a6', display: 'flex', alignItems: 'center', gap: '5px', mb: 1 }}>
                    <ImageIcon fontSize="small" />{t('wallet.admin.evidenceSlip')}
                  </Box>
                  <AuthenticatedImage
                    src={getEvidencePhotoUrl(tx.evidencePhoto.filename)}
                    alt="Evidence slip"
                    style={{ width: '100%', height: 'auto', maxHeight: '55vh', objectFit: 'contain', borderRadius: 12 }}
                  />
                </Box>
              </>
            )}

            {req && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Box sx={{ fontSize: 11.5, color: '#9b96a6', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>{t('wallet.admin.linkedTopupRequest')}</Box>
                </Divider>
                <DetailRow label={t('wallet.admin.requestId')} value={req._id?.toString()} mono />
                <DetailRow label={t('wallet.admin.status')} value={req.status} />
                {req.approvalNotes && <DetailRow label={t('wallet.admin.approvalNotes')} value={req.approvalNotes} />}
                {req.rejectionReason && <DetailRow label={t('wallet.admin.rejectionReason')} value={req.rejectionReason} />}
                <DetailRow
                  label={t('wallet.admin.reviewedBy')}
                  value={req.reviewedBy ? `${req.reviewedBy.name} — ${fmtD(req.reviewedBy.reviewedAt)}` : null}
                />
                {req.evidencePhoto && (
                  <Box mt={2}>
                    <Box sx={{ fontSize: 12, color: '#9b96a6', display: 'flex', alignItems: 'center', gap: '5px', mb: 1 }}>
                      <ImageIcon fontSize="small" />{t('wallet.admin.evidenceSlip')}
                    </Box>
                    <AuthenticatedImage
                      src={getEvidencePhotoUrl(req.evidencePhoto.filename)}
                      alt="Evidence slip"
                      style={{ width: '100%', height: 'auto', maxHeight: '55vh', objectFit: 'contain', borderRadius: 12 }}
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
